/**
 * AddDragGhost — the preview that follows the pointer while dragging from the
 * palette. Renders what you're actually about to drop (see dropPreviews):
 * a section shows its real widget layout, a widget shows itself, a page shows a
 * mini page frame. Fixed-positioned + pointer-events:none so it never interferes
 * with hit-testing the grid underneath.
 *
 * Placement depends on what's dragging (pointerType from AddDragContext):
 *   • mouse → TRAIL the preview below-right of the cursor, so the precise
 *     cursor point and the section-drop marker under it stay visible.
 *   • touch/pen → CENTER it under the pointer: the finger occludes that spot
 *     anyway, so a trailing preview would just hide under the hand.
 */
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { radii } from "@duro-app/tokens/tokens/spacing.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";
import { useAddDrag } from "./AddDragContext.js";
import { DropPreview } from "./dropPreviews.js";

// How far to trail the preview from a mouse cursor (its top-left corner).
const MOUSE_OFFSET = 14;

export function AddDragGhost() {
  const { dragging, pointer, pointerType } = useAddDrag();
  if (!dragging || !pointer) return null;

  const coarse = pointerType !== "mouse"; // touch/pen → finger sits on the point
  const transform = coarse
    ? "translate(-50%, -50%)"
    : `translate(${MOUSE_OFFSET}px, ${MOUSE_OFFSET}px)`;

  return (
    <html.div style={[s.ghost, s.at(pointer.x, pointer.y, transform)]}>
      {dragging.previewUrl ? (
        // Dragging a Media Library asset → the ghost is the asset itself.
        <html.img
          src={dragging.previewUrl}
          alt=""
          draggable={false}
          style={s.thumb}
        />
      ) : (
        <DropPreview
          kind={dragging.kind}
          templateId={dragging.templateId}
          widgetType={dragging.widgetType}
          label={dragging.label}
        />
      )}
    </html.div>
  );
}

const s = css.create({
  ghost: {
    position: "fixed",
    pointerEvents: "none",
    // The top layer: the ghost rides over every panel and Duro popup it is
    // dragged across (was 2000; nothing on web sits above layers.portal).
    zIndex: layers.portal,
    borderRadius: radii.sm,
    boxShadow: shadows.lg,
    outlineWidth: borders.focusRing,
    outlineStyle: "solid",
    outlineColor: colors.accent,
    outlineOffset: `calc(-1 * ${borders.focusOffsetSm})`,
    opacity: 0.9,
    overflow: "hidden",
  },
  at: (left: number, top: number, transform: string) => ({
    left,
    top,
    transform,
  }),
  thumb: {
    display: "block",
    width: sizes.dragThumbW,
    height: sizes.dragThumbH,
    objectFit: "cover",
  },
});
